// Player redemptions panel (plan §12) — shared by desktop Wallet and mobile.
// Lists redemption history from the new capability-driven contract, shows the public
// lifecycle (review time distinct from bank delivery), and offers cancel only when the
// server says dispatch/export has not begun (allowed_actions).
//
// Rendered only when the payout domain is enabled; the existing legacy wallet redemption
// UI is untouched so the live wallet keeps working when the domain is off.
import React, { useState } from "react";
import { fmtMoney } from "@/lib/payouts";
import { useRedemptions, usePayoutOptions } from "@/components/payouts/usePayouts";
import { RedemptionStatusChip, RedemptionTimeline } from "@/components/payouts/RedemptionStatus";
import RedeemFlow from "@/components/payouts/RedeemFlow";

export default function RedemptionsPanel() {
  const { options, reload: reloadOptions } = usePayoutOptions();
  const { items, loading, cancel, reload } = useRedemptions();
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);

  if (options && options.intake_enabled === false) {
    return (
      <div className="rounded-[16px] border border-[#1F1F23] bg-[#0C0C0E] p-4 text-[13px] text-[#a1a1aa]">
        Redemptions are not currently available. Your eligible balance is safe and support
        can help with any existing request.
      </div>
    );
  }

  const hasOpenRequest = items.some((r) =>
    !["paid", "rejected", "cancelled", "failed", "returned"].includes(r.state));

  return (
    <div className="flex flex-col gap-3">
      {options && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-[#71717a]">
          <span>Available: <b className="text-[#e4e4e7]">{fmtMoney(options.balances?.available)}</b></span>
          <span>Reserved: <b className="text-[#e4e4e7]">{fmtMoney(options.balances?.reserved)}</b></span>
          <span>Min {fmtMoney(options.limits?.min)} · Max {fmtMoney(options.limits?.max_per_request)}</span>
          <span>Review target: {options.review_target_business_days} business days</span>
        </div>
      )}

      {creating ? (
        <RedeemFlow
          options={options}
          onDone={() => { reload(); reloadOptions && reloadOptions(); }}
          onClose={() => setCreating(false)}
        />
      ) : (
        <button
          disabled={hasOpenRequest}
          onClick={() => setCreating(true)}
          className="self-start rounded-[10px] px-4 py-2 text-[13px] font-bold text-[#0A0A0A] disabled:opacity-50"
          style={{ backgroundColor: "#00D632" }}
          title={hasOpenRequest ? "You already have a redemption in progress" : undefined}>
          {hasOpenRequest ? "Redemption in progress" : "Request a redemption"}
        </button>
      )}

      {loading ? (
        <div className="py-6 text-center text-[13px] text-[#52525b]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="py-6 text-center text-[13px] text-[#52525b]">No redemptions yet.</div>
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((r) => (
            <li key={r.reference} className="rounded-[14px] border border-[#1F1F23] bg-[#0E0E11] p-3.5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-display text-[16px] font-bold">{fmtMoney(r.amount)}</div>
                  <div className="font-mono text-[11px] text-[#71717a]">{r.reference}</div>
                </div>
                <RedemptionStatusChip state={r.state} />
              </div>
              <div className="mt-3 flex items-center justify-between">
                <button
                  className="text-[12px] text-[#a1a1aa] underline-offset-2 hover:underline"
                  onClick={() => setOpen(open === r.reference ? null : r.reference)}
                >
                  {open === r.reference ? "Hide details" : "View details"}
                </button>
                {(r.allowed_actions || []).includes("cancel") && (
                  <button
                    className="text-[12px] font-semibold text-[#EF4444] disabled:opacity-50"
                    disabled={busy}
                    onClick={async () => {
                      setBusy(true);
                      try { await cancel(r.reference, "player cancelled"); } finally { setBusy(false); }
                    }}
                  >
                    Cancel
                  </button>
                )}
              </div>
              {open === r.reference && (
                <div className="mt-3 border-t border-[#1A1A1E] pt-3">
                  <RedemptionTimeline state={r.state} />
                  {(r.public_requirements || []).length > 0 && (
                    <ul className="mt-2 text-[12px] text-[#d4d4d8]">
                      {r.public_requirements.map((req, i) => (
                        <li key={i}>{req.message || req.requirement}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
